/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { useCallback, useEffect, useRef } from 'react';
import { usePreviousBooleanArray, usePreviousNumberArray } from 'react-hooks-use-previous';
import { useSelector } from 'react-redux';

import {
  gameLogger,
  selectCurrentHorseIndex,
  selectCurrentRoundWinnerHorse,
  selectCurrentState,
  selectHandiFailureHorseIndex,
  selectHorses,
  selectHorsesBonuscards,
  selectHorsesDropped,
  selectPlayerId,
  selectPlayers,
  selectRound,
} from '~/app';
import Turfmaster from '~/server/src/tmmodel';
import { useOnStateChange } from '~/utils';

/**
 * Checks the given arrays for changes and makes a comparison between the values.
 * If a change is detected the specified callback function gets called with the index
 * as a function parameter.
 *
 * @param callback function that gets executed when an array change is detected
 */
export const useArrayChangeTrigger = <T>(
  first: T[],
  second: T[],
  callback: (index: number[]) => void,
): void => {
  useEffect(() => {
    // make sure used properties are valid and we are not in the initial effect execution
    if (first && second && first.length && second.length) {
      // fire if different length or if something changed
      if (first.length !== second.length) {
        callback([]);
      } else {
        // check each property against the previous and fire with all changes detected
        const result: number[] = [];
        first.forEach((dropped, index) => {
          if (dropped !== second[index]) result.push(index);
        });
        callback(result);
      }
    }
  }, [callback, first, second]);
};

/**
 * Checks the local redux game state for changes in horsesDropped and makes a comparison
 * with the previous version. If a change is detected the specified callback function gets
 * called with the horseId as the function parameter.
 *
 * @param callback function that gets executed when a horse is dropped with the horse id
 */
export const useHorseDroppedTrigger = (callback: (horseIndex: number) => void): void => {
  const horsesDropped = useSelector(selectHorsesDropped);
  const prevHorsesDropped = usePreviousBooleanArray(horsesDropped);
  useArrayChangeTrigger(horsesDropped, prevHorsesDropped, (horseIndices: number[]) => {
    if (horseIndices.length && horsesDropped[horseIndices[0]]) callback(horseIndices[0]);
  });
};

/**
 * Checks the local redux game state for changes in horsesFinished and makes a comparison
 * with the previous version. If a change is detected the specified callback function gets
 * called with the horseId if the horse is actually the winner of the current round
 * as the function parameter.
 *
 * @param callback function that gets executed when a horse has finished with the horse id
 */
export const useHorseWinsTrigger = (callback: (horseIndex: number) => void): void => {
  const currentRoundWinnerHorse = useSelector(selectCurrentRoundWinnerHorse);
  const prevCurrentRoundWinnerHorse = useRef(currentRoundWinnerHorse);

  useEffect(() => {
    if (
      prevCurrentRoundWinnerHorse.current !== currentRoundWinnerHorse &&
      currentRoundWinnerHorse >= 0
    )
      callback(currentRoundWinnerHorse);
  }, [callback, currentRoundWinnerHorse]);

  useEffect(() => {
    prevCurrentRoundWinnerHorse.current = currentRoundWinnerHorse;
  }, [currentRoundWinnerHorse]);
};

/**
 * Checks the local redux game state if the bonus cards of a horse got automatically merged in the last round because
 * they weren't used before by the player.
 *
 * @param callback gets exectuted when the bonus cards of one of the players' horses get automatically merged with the
 * associated horseid as a parameter
 */
export const useOwnHorseAutomatedBonusCardsInLastRoundTrigger = (
  callback: (horseIndex: number) => void,
): void => {
  const playerId = useSelector(selectPlayerId);
  const horses = useSelector(selectHorses);
  const round = useSelector(selectRound);
  const horseBonusCards = useSelector(selectHorsesBonuscards);
  const prevHorseBonusCards = usePreviousNumberArray(horseBonusCards);

  useArrayChangeTrigger(horseBonusCards, prevHorseBonusCards, (horseIndices: number[]) => {
    if (
      round === Turfmaster.rounds - 1 &&
      horseIndices.length &&
      horses[horseIndices[0]].playerId === playerId &&
      horseBonusCards[horseIndices[0]] === round
    )
      callback(horseIndices[0]);
  });
};

/**
 * Checks the local redux game state for changes in the round property and triggers the callback if a new round started.
 * Ignores the first (index = 0) and last (index = 3) round.
 *
 * @param callback gets executed when a new round starts with the round index as a paramter
 */
export const useStartRoundTrigger = (callback: (round: number) => void): void => {
  const currentState = useSelector(selectCurrentState);
  const round = useSelector(selectRound);
  useOnStateChange(
    currentState,
    useCallback(
      (newState) => {
        if (
          newState === Turfmaster.stateMachine.STARTROUND &&
          round > 0 &&
          round < Turfmaster.rounds
        ) {
          callback(round);
        }
      },
      [callback, round],
    ),
  );
};

/**
 * Checks the local redux game state for changes in the handicap failure flag and fires if it is true.
 * Evaluation of flag is executed in gameUpdateSaga.ts.
 *
 * @param callback gets executed when a horse has used a card which is higher or equal than the horse's handicap
 */
export const useCardAboveHandiTrigger = (callback: (horseIndex: number) => void): void => {
  const handiFailureHorseIndex = useSelector(selectHandiFailureHorseIndex);
  const prevHandiFailureHorseIndex = useRef(handiFailureHorseIndex);

  useEffect(() => {
    if (
      prevHandiFailureHorseIndex.current !== handiFailureHorseIndex &&
      handiFailureHorseIndex >= 0
    ) {
      callback(handiFailureHorseIndex);
    }
  }, [callback, handiFailureHorseIndex, prevHandiFailureHorseIndex]);

  useEffect(() => {
    prevHandiFailureHorseIndex.current = handiFailureHorseIndex;
  }, [handiFailureHorseIndex]);
};

/**
 * Triggers when ADDBONUSCARDSOPP event is fired.
 *
 * @param callback gets executed when a horse has used a card which is higher or equal than the horse's handicap
 */
export const useBonusCardTrigger = (callback: (horseIndex: number) => void): void => {
  const currentState = useSelector(selectCurrentState);
  const currentHorseIndex = useSelector(selectCurrentHorseIndex);

  useOnStateChange(
    currentState,
    useCallback(
      (newState) => {
        gameLogger.debug({ newState, state: Turfmaster.stateMachine.ADDBONUSCARDSOPP }, 'triggers');
        if (newState === Turfmaster.stateMachine.ADDBONUSCARDSOPP) callback(currentHorseIndex);
      },
      [callback, currentHorseIndex],
    ),
  );
};

/**
 * Triggers when JOINGAMEOPP event is fired (player joins remote game).
 *
 * @param callback gets executed when a player joins a remote game with the player name as parameter
 */
export const usePlayerJoinedTrigger = (callback: (playerName: string) => void): void => {
  const currentState = useSelector(selectCurrentState);
  const players = useSelector(selectPlayers);
  const playerId = useSelector(selectPlayerId);

  useOnStateChange(
    currentState,
    useCallback(
      (newState) => {
        gameLogger.debug({ newState, state: Turfmaster.stateMachine.JOINGAMEOPP }, 'triggers');
        if (newState === Turfmaster.stateMachine.JOINGAMEOPP && players.length > 0) {
          // Get the most recently added player (last in the array)
          const latestPlayer = players[players.length - 1];
          // Only show notification if the joined player is not the current player
          if (latestPlayer && latestPlayer.name && latestPlayer.id !== playerId) {
            callback(latestPlayer.name);
          }
        }
      },
      [callback, players, playerId],
    ),
  );
};

/**
 * Triggers when SKIPPLAYER event is fired (player skipped due to no valid moves).
 *
 * @param callback gets executed when a player is skipped with the horse index as parameter
 */
export const usePlayerSkippedTrigger = (callback: (horseIndex: number) => void): void => {
  const currentState = useSelector(selectCurrentState);
  const currentHorseIndex = useSelector(selectCurrentHorseIndex);

  useOnStateChange(
    currentState,
    useCallback(
      (newState) => {
        gameLogger.debug({ newState, state: Turfmaster.stateMachine.SKIPPLAYER }, 'triggers');
        if (newState === Turfmaster.stateMachine.SKIPPLAYER) callback(currentHorseIndex);
      },
      [callback, currentHorseIndex],
    ),
  );
};

/**
 * Triggers when LEAVEGAMEOPP event is fired (player leaves remote game).
 *
 * @param callback gets executed when a player leaves a remote game with the player name as parameter
 */
export const usePlayerLeftTrigger = (callback: (playerName: string) => void): void => {
  const currentState = useSelector(selectCurrentState);
  const players = useSelector(selectPlayers);
  const prevPlayers = useRef(players);

  useOnStateChange(
    currentState,
    useCallback(
      (newState) => {
        if (newState === Turfmaster.stateMachine.LEAVEGAMEOPP) {
          // Store the previous players array before comparison to avoid race conditions
          const previousPlayers = prevPlayers.current;
          // Find which player left by comparing previous and current player arrays
          if (previousPlayers && previousPlayers.length > players.length) {
            // Find the player that was removed
            const leftPlayer = previousPlayers.find(
              (prevPlayer) => !players.find((p) => p.userId === prevPlayer.userId),
            );
            if (leftPlayer && leftPlayer.name) {
              callback(leftPlayer.name);
            }
          }
          // Update previous players reference after processing
          prevPlayers.current = players;
        }
      },
      [callback, players],
    ),
  );

  // Update previous players reference when players change but state is not LEAVEGAMEOPP
  // This ensures the ref stays current for future comparisons
  useEffect(() => {
    if (currentState !== Turfmaster.stateMachine.LEAVEGAMEOPP) {
      prevPlayers.current = players;
    }
  }, [players, currentState]);
};
